import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Loader2,
  Sparkles,
  Search,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Globe,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

interface SearchTerm {
  term: string;
  rationale: string;
  searchType: 'category' | 'feature' | 'comparison' | 'intent';
}

interface Competitor {
  domain: string;
  url: string;
  title: string;
  snippet: string;
  foundIn: string[];
  relevanceScore: number;
  suggested: boolean;
}

interface CompetitorDiscoveryProps {
  productName: string;
  productCategory?: string;
  productId?: string;
  country: string;
  ownDomain?: string;
  onCompetitorsSelected: (urls: string[]) => void;
  disabled?: boolean;
}

const SEARCH_TYPE_COLORS: Record<string, string> = {
  category: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  feature: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  comparison: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  intent: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

export function CompetitorDiscovery({
  productName,
  productCategory,
  productId,
  country,
  ownDomain,
  onCompetitorsSelected,
  disabled = false,
}: CompetitorDiscoveryProps) {
  // #3847 iter-8: the backend requires a siteId whenever productId is sent
  // (tenant-scoped product lookup). Thread it from the global SiteContext.
  const { selectedSite } = useSite();

  // Step 1: Search terms
  const [searchTerms, setSearchTerms] = useState<SearchTerm[]>([]);
  const [selectedTerms, setSelectedTerms] = useState<Set<string>>(new Set());
  const [generatingTerms, setGeneratingTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [step1Open, setStep1Open] = useState(true);

  // Step 2: Competitors
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [excludedDomains, setExcludedDomains] = useState<Array<{ domain: string; reason: string }>>([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set());
  const [discoveringCompetitors, setDiscoveringCompetitors] = useState(false);
  const [competitorsError, setCompetitorsError] = useState<string | null>(null);
  const [step2Open, setStep2Open] = useState(false);

  const MAX_COMPETITORS = 3;

  // Generate search terms using AI
  const generateSearchTerms = async () => {
    if (!productName.trim()) {
      setTermsError('Product name is required');
      return;
    }

    // #3847 iter-10 / follow-up: the backend requires a siteId whenever productId
    // is present. Normalize the SiteContext id — older sites carry a legacy `_id`
    // shape (same fallback the rest of the app uses).
    const siteId = selectedSite?.id || selectedSite?._id;
    if (productId && !siteId) {
      setTermsError('Select a site before generating search terms for this product.');
      return;
    }

    // Debug logging
    console.log('[CompetitorDiscovery] Generating search terms with:', {
      productName,
      productCategory,
      productId,
    });

    if (!productCategory) {
      console.warn('[CompetitorDiscovery] No category available - search terms may be inaccurate');
    }

    setGeneratingTerms(true);
    setTermsError(null);
    setSearchTerms([]);
    setSelectedTerms(new Set());

    try {
      const response = await authFetch('/api/aeo-benchmark/suggest-search-terms', {
        method: 'POST',
        body: JSON.stringify({
          productName,
          productCategory: productCategory || '',
          productId: productId || undefined,
          // #3847 iter-8 / follow-up: required by the backend when productId is
          // present; normalized above to cover the legacy _id shape.
          siteId: productId ? siteId : undefined,
        }),
      });

      const data = await handleResponse<{ searchTerms: SearchTerm[] }>(response);

      setSearchTerms(data.searchTerms || []);

      // Auto-select first 2 terms
      const autoSelect = new Set(data.searchTerms.slice(0, 2).map((t) => t.term));
      setSelectedTerms(autoSelect);
    } catch (err: any) {
      console.error('Failed to generate search terms:', err);
      setTermsError(err.message || 'Failed to generate search terms');
    } finally {
      setGeneratingTerms(false);
    }
  };

  // Toggle search term selection
  const toggleTermSelection = (term: string) => {
    setSelectedTerms((prev) => {
      const next = new Set(prev);
      if (next.has(term)) {
        next.delete(term);
      } else {
        next.add(term);
      }
      return next;
    });
  };

  // Discover competitors using selected search terms
  const findCompetitors = async () => {
    if (selectedTerms.size === 0) {
      setCompetitorsError('Select at least one search term');
      return;
    }

    setDiscoveringCompetitors(true);
    setCompetitorsError(null);
    setCompetitors([]);
    setSelectedCompetitors(new Set());
    setStep2Open(true);

    try {
      const response = await authFetch('/api/aeo-benchmark/discover-competitors', {
        method: 'POST',
        body: JSON.stringify({
          searchTerms: Array.from(selectedTerms),
          country,
          ownDomain: ownDomain || '',
        }),
      });

      const data = await handleResponse<{
        competitors: Competitor[];
        excluded: Array<{ domain: string; reason: string }>;
      }>(response);

      setCompetitors(data.competitors || []);
      setExcludedDomains(data.excluded || []);

      // Auto-select suggested competitors (top 3 by relevance)
      const autoSelect = new Set(
        data.competitors
          .filter((c) => c.suggested)
          .slice(0, MAX_COMPETITORS)
          .map((c) => c.url)
      );
      setSelectedCompetitors(autoSelect);

      // Notify parent of selection
      onCompetitorsSelected(Array.from(autoSelect));
    } catch (err: any) {
      console.error('Failed to discover competitors:', err);
      setCompetitorsError(err.message || 'Failed to discover competitors');
    } finally {
      setDiscoveringCompetitors(false);
    }
  };

  // Toggle competitor selection
  const toggleCompetitorSelection = (url: string) => {
    setSelectedCompetitors((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else if (next.size < MAX_COMPETITORS) {
        next.add(url);
      }
      // Update parent
      onCompetitorsSelected(Array.from(next));
      return next;
    });
  };

  const hasOwnSiteExcluded = excludedDomains.some((e) => e.reason === 'own_site');

  return (
    <div className="space-y-4">
      {/* Step 1: Generate Search Terms */}
      <Collapsible open={step1Open} onOpenChange={setStep1Open}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="flex w-full justify-between p-4 border rounded-lg hover:bg-accent"
          >
            <div className="flex items-center gap-2">
              {step1Open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="font-medium">Step 1: Generate Search Terms</span>
              {searchTerms.length > 0 && !step1Open && (
                <Badge variant="secondary" className="ml-2">
                  {selectedTerms.size} selected
                </Badge>
              )}
            </div>
            {searchTerms.length > 0 && (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="px-4 pb-4 pt-2 border-x border-b rounded-b-lg">
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-1">
              <div>
                Product: <span className="font-medium text-foreground">{productName}</span>
              </div>
              {productCategory && (
                <div>
                  Category: <span className="font-medium text-foreground">{productCategory}</span>
                </div>
              )}
            </div>

            {!productCategory && searchTerms.length === 0 && (
              <Alert className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                  <strong>Warning:</strong> No product category available. Search terms may be
                  inaccurate. Make sure the product has been enriched.
                </AlertDescription>
              </Alert>
            )}

            {searchTerms.length === 0 ? (
              <Button
                onClick={generateSearchTerms}
                disabled={disabled || generatingTerms || !productName.trim()}
                className="w-full"
              >
                {generatingTerms ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Search Terms
                  </>
                )}
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  {searchTerms.map((term, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTerms.has(term.term)
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => toggleTermSelection(term.term)}
                    >
                      <Checkbox
                        checked={selectedTerms.has(term.term)}
                        onCheckedChange={() => toggleTermSelection(term.term)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">"{term.term}"</span>
                          <Badge
                            variant="outline"
                            className={SEARCH_TYPE_COLORS[term.searchType] || ''}
                          >
                            {term.searchType}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{term.rationale}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateSearchTerms}
                    disabled={disabled || generatingTerms}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Regenerate
                  </Button>

                  <Button
                    onClick={findCompetitors}
                    disabled={disabled || discoveringCompetitors || selectedTerms.size === 0}
                  >
                    {discoveringCompetitors ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Finding Competitors...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Find Competitors ({selectedTerms.size} selected)
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {termsError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{termsError}</AlertDescription>
              </Alert>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Step 2: Select Competitors */}
      {(competitors.length > 0 || discoveringCompetitors) && (
        <Collapsible open={step2Open} onOpenChange={setStep2Open}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="flex w-full justify-between p-4 border rounded-lg hover:bg-accent"
            >
              <div className="flex items-center gap-2">
                {step2Open ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span className="font-medium">Step 2: Select Competitors</span>
                {competitors.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedCompetitors.size}/{MAX_COMPETITORS} selected
                  </Badge>
                )}
              </div>
              {selectedCompetitors.size > 0 && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="px-4 pb-4 pt-2 border-x border-b rounded-b-lg">
            <div className="space-y-4">
              {discoveringCompetitors ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="ml-3 text-muted-foreground">
                    Running SERP queries... This may take a few seconds.
                  </span>
                </div>
              ) : competitors.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No competitors found. Try different search terms or check your product name.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    Found {competitors.length} competitors
                    {hasOwnSiteExcluded && (
                      <span className="ml-2 text-green-600">(your site excluded)</span>
                    )}
                  </div>

                  {selectedCompetitors.size >= MAX_COMPETITORS && (
                    <Alert>
                      <AlertDescription>
                        Maximum {MAX_COMPETITORS} competitors selected. Deselect one to choose
                        another.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    {competitors.map((competitor, index) => {
                      const isSelected = selectedCompetitors.has(competitor.url);
                      const isDisabled =
                        !isSelected && selectedCompetitors.size >= MAX_COMPETITORS;

                      return (
                        <div
                          key={index}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : isDisabled
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer hover:bg-accent'
                          }`}
                          onClick={() => !isDisabled && toggleCompetitorSelection(competitor.url)}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() =>
                              !isDisabled && toggleCompetitorSelection(competitor.url)
                            }
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium truncate">{competitor.domain}</span>
                              <Badge variant="outline">Score: {competitor.relevanceScore}</Badge>
                              {competitor.suggested && (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Suggested
                                </Badge>
                              )}
                              <Badge variant="secondary">
                                Found in {competitor.foundIn.length} quer
                                {competitor.foundIn.length === 1 ? 'y' : 'ies'}
                              </Badge>
                            </div>
                            <p className="text-sm font-medium mt-1 truncate">{competitor.title}</p>
                            {competitor.snippet && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {competitor.snippet}
                              </p>
                            )}
                            <a
                              href={competitor.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {competitor.url.length > 60
                                ? competitor.url.slice(0, 60) + '...'
                                : competitor.url}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {competitorsError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{competitorsError}</AlertDescription>
                </Alert>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
